import parseISO from 'date-fns/parseISO';
import {graphql} from 'gatsby';
import * as React from 'react';
import {Helmet} from 'react-helmet-async';

import SiteHeader from '@src/components/SiteHeader';
import ButtonPrint from '@src/components/Button/ButtonPrint';
import Footer from '@src/components/Footer';
import {NowProvider} from '@src/components/NowProvider';
import {getLanguageByCode, getLanguageCodeFromPathname} from '@src/i18n/utils';
import Layout from '@src/components/Layouts';
import {formatLastUpdatedDate} from '@src/utils/metadata';

import type {PageLayoutFragment} from '@graphql-types';
import type {WindowLocation} from '@reach/router';
import type {NavItem} from '@src/components/Nav';
import type {Languages, Translations} from '@src/i18n';

export interface PageLayoutProps {
  children: React.ReactNode;
  languages: Languages[];
  lastBuildTime: Date;
  lastUpdatedDate?: string;
  location: WindowLocation;
  navigation: PageLayoutFragment['allPrismicMainNavigation'];
  notifications: PageLayoutFragment['allPrismicNotifications'];
  siteName: string;
  siteUrl: string;
  translations?: Translations[];
}

const ensurePrecedingSlash = (s: string) => (s[0] === '/' ? s : `/${s}`);

export const getTranslations = (
  pages: Array<{
    lang: string;
    path: string;
  }>,
  languages: Languages[],
) =>
  pages
    // for an unclear reason, null elements can come through in addition to the expected values
    .filter(p => Boolean(p))
    .map(p => ({
      href: p.path,
      lang: getLanguageByCode(p.lang, languages),
    }));

const PageLayout = ({
  children,
  languages,
  lastBuildTime,
  lastUpdatedDate,
  location,
  navigation,
  notifications,
  siteName,
  siteUrl,
  translations,
  ...divProps
}: PageLayoutProps & React.HTMLProps<HTMLDivElement>) => {
  const initialLanguageCode = getLanguageCodeFromPathname(
    location.pathname,
    languages,
  );

  // Get navigation node based on language code
  const navNode = navigation.edges.filter(edge => {
    return edge.node.lang === initialLanguageCode;
  });

  const {nav} = navNode[0].node.data;

  return (
    // it's not ideal to insert a provider here
    // ideally, we would use the wrapRootElement API
    // however, we want to be able to source the initial value (i.e., used for server-side rendering) from site metadata
    <NowProvider value={lastBuildTime}>
      <Layout
        languages={languages}
        location={location}
        notifications={notifications}
        siteName={siteName}
        siteUrl={siteUrl}>
        <Helmet>
          {translations?.map(trans => (
            <link
              key={trans.lang.code}
              rel="alternate"
              hrefLang={trans.lang.code}
              href={`${siteUrl}${ensurePrecedingSlash(trans.href)}`}
            />
          ))}
        </Helmet>
        <div {...divProps}>
          <SiteHeader translations={translations} nav={nav as NavItem[]} />
          {children}
          <ButtonPrint />
          <Footer
            lastBuildTime={lastBuildTime}
            lastUpdatedDate={lastUpdatedDate}
          />
        </div>
      </Layout>
    </NowProvider>
  );
};

/**
 * Return both date props for the PageLayout component, from the ISO-formatted
 * date string that should power these values for most pages.
 */
PageLayout.dateProps = (lastBuildTime: string) => ({
  lastBuildTime: parseISO(lastBuildTime),
  lastUpdatedDate: formatLastUpdatedDate(parseISO(lastBuildTime)),
});

export default PageLayout;

export const query = graphql`
  fragment PageLayout on Query {
    allLanguages {
      nodes {
        code
        label
        name
        path
      }
    }
    allPrismicMainNavigation {
      edges {
        node {
          lang
          data {
            nav {
              ... on PrismicMainNavigationDataNavNavItem {
                primary {
                  label {
                    text
                  }
                  link {
                    text
                  }
                }
                items {
                  sub_nav_link_label {
                    text
                  }
                  sub_nav_link_url {
                    text
                  }
                }
              }
            }
          }
        }
      }
    }
    allPrismicNotifications {
      edges {
        node {
          lang
          id
          data {
            show_hide_notification
            text {
              html
            }
          }
        }
      }
    }
    site {
      siteMetadata {
        lastBuildTime
        siteName
        siteUrl
      }
    }
  }
`;
